const KNOWN_SECTIONS = new Set([
  'Community highlights',
  'Tournament status',
  'Recent results',
  'Upcoming schedule',
  'Follow the team',
  'Matches',
  'Practices',
]);

const STANDING_ROW = /^\s*(\d+)\.\s+(.+?)\s+—\s+(\d+)\s+pts\s+\((.+)\)\s*$/;
const BULLET_LINE = /^•\s*(.+)$/;
const FIXTURE_LINE = /^\s*-\s*(.+)$/;
const GOAL_LINE = /^\s{4,}(.+)$/;

function isSectionTitle(line) {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('Last updated:')) return false;
  if (trimmed.startsWith('Official ')) return false;
  if (trimmed.startsWith('Sources:')) return false;
  if (trimmed.startsWith('•')) return false;
  if (trimmed.startsWith('-')) return false;
  if (trimmed.startsWith('Top scorers:')) return false;
  if (trimmed.startsWith('Live scores:')) return false;
  if (/^\s{2,}/.test(line) && !trimmed.startsWith('Top scorers')) return false;
  if (KNOWN_SECTIONS.has(trimmed)) return true;
  if (trimmed.startsWith('Maveli Cup')) return true;
  if (trimmed.includes('standings')) return true;
  if (trimmed.startsWith('Columbus Tuskers —')) return true;
  if (trimmed.startsWith('Full ')) return true;
  return false;
}

function parseStandingRow(line) {
  const match = line.match(STANDING_ROW);
  if (!match) return null;
  const [, position, club, points, record] = match;
  const recordMatch = record.match(/(\d+)\s+played,\s*(\d+)W\s*(\d+)D\s*(\d+)L,\s*(\d+)[–-](\d+)/);
  return {
    position,
    club,
    points: Number(points),
    played: recordMatch ? Number(recordMatch[1]) : null,
    wins: recordMatch ? Number(recordMatch[2]) : null,
    draws: recordMatch ? Number(recordMatch[3]) : null,
    losses: recordMatch ? Number(recordMatch[4]) : null,
    goalsFor: recordMatch ? Number(recordMatch[5]) : null,
    goalsAgainst: recordMatch ? Number(recordMatch[6]) : null,
    rawRecord: record,
  };
}

function parseListItem(text) {
  const live = /🔴\s*LIVE|\(LIVE\)/i.test(text);
  const scheduled = /\(scheduled\)/i.test(text);
  const scoreMatch = text.match(/\((\d+\s*[–-]\s*\d+)\)/);
  return {
    text: text.replace(/\s*🔴\s*LIVE/gi, '').replace(/\(scheduled\)/gi, '').trim(),
    live,
    scheduled,
    score: scoreMatch ? scoreMatch[1] : null,
  };
}

function createSection(title, kind = 'default') {
  return {
    title,
    kind,
    links: [],
    paragraphs: [],
    standings: [],
    items: [],
    tournaments: [],
    currentTournament: null,
  };
}

export function parseTuskersStatusBody(body) {
  if (!body) {
    return { lastUpdated: null, sections: [] };
  }

  const lines = body.split('\n');
  const result = { lastUpdated: null, sections: [] };
  let section = null;

  const pushSection = () => {
    if (!section) return;
    const hasContent = section.paragraphs.length
      || section.standings.length
      || section.items.length
      || section.tournaments.length
      || section.links.length;
    if (hasContent || section.title) {
      result.sections.push(section);
    }
    section = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      continue;
    }

    if (trimmed.startsWith('Last updated:')) {
      result.lastUpdated = trimmed.replace('Last updated:', '').trim();
      continue;
    }

    if (isSectionTitle(trimmed)) {
      pushSection();
      const kind = trimmed.startsWith('Maveli Cup')
        ? 'maveli'
        : trimmed.includes('standings')
          ? 'standings'
          : trimmed.startsWith('Columbus Tuskers —') || trimmed.startsWith('Full ')
            ? 'matches'
            : trimmed === 'Tournament status'
              ? 'tournaments'
              : trimmed === 'Upcoming schedule' || trimmed === 'Matches' || trimmed === 'Practices'
                ? 'schedule'
                : trimmed === 'Follow the team'
                  ? 'links'
                  : trimmed === 'Community highlights'
                    ? 'highlights'
                    : 'default';
      section = createSection(trimmed, kind);
      continue;
    }

    if (!section) {
      section = createSection('Overview', 'default');
    }

    if (trimmed.startsWith('Official standings:') || trimmed.startsWith('Official schedule:')) {
      const [label, url] = trimmed.split(':').map((part, index, arr) => (
        index === 0 ? part : arr.slice(1).join(':').trim()
      ));
      section.links.push({ label: label.trim(), url: url.trim() });
      continue;
    }

    const standing = parseStandingRow(line);
    if (standing) {
      section.kind = 'standings';
      section.standings.push(standing);
      continue;
    }

    const bullet = trimmed.match(BULLET_LINE);
    if (bullet) {
      if (section.kind === 'links') {
        const [labelPart, urlPart] = bullet[1].split(':').map((part) => part.trim());
        section.links.push({ label: labelPart, url: urlPart || labelPart });
      } else if (section.kind === 'tournaments') {
        section.currentTournament = {
          title: bullet[1],
          fixtures: [],
          topScorers: null,
          liveUrl: null,
        };
        section.tournaments.push(section.currentTournament);
      } else {
        section.items.push(parseListItem(bullet[1]));
      }
      continue;
    }

    const fixture = line.match(FIXTURE_LINE);
    if (fixture && section.currentTournament) {
      section.currentTournament.fixtures.push(parseListItem(fixture[1]));
      continue;
    }

    const goal = line.match(GOAL_LINE);
    if (goal && section.currentTournament?.fixtures?.length) {
      const lastFixture = section.currentTournament.fixtures[section.currentTournament.fixtures.length - 1];
      lastFixture.goals = lastFixture.goals || [];
      lastFixture.goals.push(goal[1]);
      continue;
    }

    if (trimmed.startsWith('Top scorers:') && section.currentTournament) {
      section.currentTournament.topScorers = trimmed.replace('Top scorers:', '').trim();
      continue;
    }

    if (trimmed.startsWith('Live scores:') && section.currentTournament) {
      section.currentTournament.liveUrl = trimmed.replace('Live scores:', '').trim();
      continue;
    }

    if (trimmed.startsWith('Sources:')) {
      section.paragraphs.push(trimmed);
      continue;
    }

    section.paragraphs.push(trimmed);
  }

  pushSection();
  return result;
}
